/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { TAB_IDS } from "constants/tabIds"

import TabContainer from "components/TabContainer"
import { selectUiTables } from "features/results/ResultsSlice"
import React from "react"
import { useSelector } from "react-redux"
import { UiTable } from "services/computations/types"

import FinalValues from "./FinalValues"

// AIDEV-NOTE: `data` reuses UiTable rather than restating its shape, so the readonly
// column contract stays in one place.
export interface Table {
  readonly id: string
  readonly caption: string
  readonly data: Readonly<UiTable>
  readonly category: "failure" | "consumption" | "rates"
}

export interface ResultsTabsProps {
  readonly failureRatesTables: readonly Table[]
  readonly consumptionAmountTables: readonly Table[]
  readonly consumptionRatesTables: readonly Table[]
  readonly areResultsFresh: boolean | null
  readonly renderTableGroup: (tables: readonly Table[]) => React.ReactNode
}

const ResultsTabs: React.FC<ResultsTabsProps> = ({
  failureRatesTables,
  consumptionAmountTables,
  consumptionRatesTables,
  areResultsFresh,
  renderTableGroup,
}) => {
  // Check if final values distribution data is available
  const { uiTables } = useSelector(selectUiTables)
  const finalValuesAvailable = !!uiTables?.finalValuesDistribution
  return (
    <TabContainer
      key="results-tab-container"
      defaultActiveTab={finalValuesAvailable ? TAB_IDS.FINAL_VALUES : TAB_IDS.FAILURE_RATES}
      tabs={[
        // Final Values tab - conditionally included as first tab
        ...(finalValuesAvailable
          ? [
              {
                id: TAB_IDS.FINAL_VALUES,
                title: "Final Values",
                errorCount: 0,
                children: (
                  <div
                    className="results-section"
                    aria-describedby={!areResultsFresh ? "stale-message" : undefined}
                    aria-live="polite"
                  >
                    <FinalValues />
                  </div>
                ),
              },
            ]
          : []),
        {
          id: TAB_IDS.FAILURE_RATES,
          title: "Failure Rates",
          children: (
            <div
              className="results-section"
              aria-describedby={!areResultsFresh ? "stale-message" : undefined}
              aria-live="polite"
            >
              {renderTableGroup(failureRatesTables)}
            </div>
          ),
          errorCount: 0,
        },
        {
          id: TAB_IDS.CONSUMPTION_AMOUNTS,
          title: "Consumption Amounts",
          children: (
            <div
              className="results-section"
              aria-describedby={!areResultsFresh ? "stale-message" : undefined}
              aria-live="polite"
            >
              {renderTableGroup(consumptionAmountTables)}
            </div>
          ),
          errorCount: 0,
        },
        {
          id: TAB_IDS.CONSUMPTION_RATES,
          title: "Consumption Rates",
          children: (
            <div
              className="results-section"
              aria-describedby={!areResultsFresh ? "stale-message" : undefined}
              aria-live="polite"
            >
              {renderTableGroup(consumptionRatesTables)}
            </div>
          ),
          errorCount: 0,
        },
        {
          id: TAB_IDS.ABOUT_RESULTS,
          title: "About Results",
          errorCount: 0,
          children: (
            <div className="about-results-section">
              <div className="card mt-2 mb-4">
                <div className="card-header">
                  <h5 className="card-title mb-0">Understanding These Results</h5>
                </div>
                <div className="card-body">
                  <p>
                    These results are based on historical retirements with your specified
                    parameters.
                  </p>
                  <p>
                    Table columns give results for retirements that began within particular periods
                    of time and/or share initial market conditions, including a column pertaining to
                    the current Historical Data Selection ranges.
                  </p>
                  <p>
                    For example, a column header of `CAPE &lt; 20` indicates that the column
                    contains results derived exclusively from simulated retirements in which the
                    CAPE ratio in the starting month was below 20.
                  </p>
                  <p>
                    The failure rates are the percentages of historical retirements with your
                    specified parameters that would have ended with a portfolio total lower than
                    your specified final target.
                  </p>
                  <p>
                    <i>
                      Consumption amounts and rates are calculated net of any cash flows you&apos;ve
                      specified.
                    </i>
                  </p>
                </div>
              </div>

              <div className="card mb-2">
                <div className="card-header">
                  <h5 className="card-title mb-0">Interpreting the Tables</h5>
                </div>
                <div className="card-body">
                  <p>The tables show different perspectives on the same underlying data:</p>
                  <ul className="mb-0">
                    <li className="mb-2">
                      <strong>Failure Rates</strong> - Shows the percentage of historical scenarios
                      where your portfolio would have reached your specified final target by the end
                      of your retirement.
                    </li>
                    <li className="mb-2">
                      <strong>Consumption Amounts</strong> - Shows initial consumption amounts for
                      various failure rates.
                    </li>
                    <li className="mb-0">
                      <strong>Consumption Rates</strong> - Shows initial consumption rates for
                      various failure rates.
                    </li>
                  </ul>
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="card-title mb-0">Glossary</h5>
                </div>
                <div className="card-body">
                  <dl className="row mb-0">
                    <dt className="col-sm-3">Consumption</dt>
                    <dd className="col-sm-9">
                      &quot;Consumption&quot;, as opposed to &quot;withdrawal&quot;, signifies that
                      the values are net of/excluding any cash flows your specified. Bear this in
                      mind when comparing e.g. your consumption rate to typically cited withdrawal
                      rates, like the 4% safe withdrawal rate.
                    </dd>

                    <dt className="col-sm-3">CAPE</dt>
                    <dd className="col-sm-9">
                      Cyclically Adjusted Price-to-Earnings ratio. A valuation measure that uses
                      real earnings per share over a period of time, typically 10 years, to smooth
                      out fluctuations in corporate profits. A good place to start reading up on the
                      significance of this metric is{" "}
                      <a href="https://earlyretirementnow.com/2016/12/21/the-ultimate-guide-to-safe-withdrawal-rates-part-3-equity-valuation/">
                        The Ultimate Guide to Safe Withdrawal Rates – Part 3: Equity Valuation
                      </a>
                      .
                    </dd>

                    <dt className="col-sm-3">Drawdown</dt>
                    <dd className="col-sm-9">
                      S&P 500 decline from a previous peak. For example, a 20% drawdown means the
                      index has fallen 20% from its previous highest point.
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          ),
        },
      ]}
    />
  )
}

export default ResultsTabs
